"use client";

import { useState } from "react";
import { Button } from "../Button";
import { Attachment, Close } from "@/icons";
import clsx from "clsx";
import "./DocumentUploadComponent.scss";
import { Col } from "@/components";
import { HeadingLevel } from "@/types/headings";

interface DocumentUploadProps {
  onFileUpload: (
    base64File: string,
    fileName: string,
    fileType: string,
  ) => void;
  isLoading: boolean;
  title: string;
  description?: string;
  titleTag: HeadingLevel;
  actionText?: string;
  className?: string;
  classNameTitle?: string;
  classNameDescription?: string;
  id?: string;
  name?: string;
  fileSizeLabel?: string;
  removeLabel?: string;
  accept?: string;
}

export const DocumentUploadComponent = ({
  onFileUpload,
  isLoading,
  title,
  description = "",
  titleTag: TitleTag = "h1",
  actionText = "Aggiungi",
  className = "",
  classNameTitle = "",
  classNameDescription = "",
  id = "fileInput",
  name = "fileInput",
  fileSizeLabel = "Dimensioni",
  removeLabel = "RIMUOVI",
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png",
}: DocumentUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);

      if (onFileUpload && file) {
        convertToBase64(file).then((base64) => {
          onFileUpload(base64, file.name, file.type);
        });
      }
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === "string") {
          // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
          const base64String = reader.result.split(",")[1];
          resolve(base64String);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleClear = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById(id) as HTMLInputElement;
    if (fileInput) fileInput.value = "";
    onFileUpload("", "", "");
  };
  return (
    <Col
      br={"x4"}
      gap={"x16"}
      bg={"lightblue-a03"}
      className={clsx(className, "ui-banner-upload shadow")}
    >
      <TitleTag
        className={clsx(
          selectedFile ? "text-primary" : "text-slate-20",
          "ui-banner-title-upload ",
          classNameTitle || "fs-4 fw-bold",
        )}
      >
        {selectedFile ? selectedFile.name : title}
      </TitleTag>
      <main
        className={clsx(
          "ui-banner-content-upload fs-6 text-secondary w-100 ui-banner-content-layout1-upload text-secondary justify-content-between",
        )}
      >
        <div
          className={clsx(classNameDescription, "ui-banner-description-upload")}
        >
          {selectedFile ? (
            <div>
              {fileSizeLabel}: {(selectedFile.size / (1024 * 1024)).toFixed(2)}{" "}
              MB
            </div>
          ) : (
            description
          )}
        </div>

        <div className={clsx("ui-banner-link-upload")}>
          <input
            type="file"
            id={id}
            name={name}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept={accept}
            className="fs-7"
            required
          />

          {selectedFile ? (
            <Button
              size={"2"}
              textColor="primary"
              bg="lightblue-a03"
              className={"w-full "}
              onClick={handleClear}
              disabled={isLoading}
            >
              <Close />
              <p className={"fs-6 fw-bold px-x8"}>{removeLabel}</p>
            </Button>
          ) : (
            <Button
              size={"2"}
              textColor="primary"
              bg="lightblue-a03"
              border={"primary"}
              className={"w-full"}
              onClick={() => document.getElementById(id)?.click()}
              disabled={isLoading}
            >
              <Attachment className={"mr-x8"} />
              <p className={"fs-6 fw-bold"}>{actionText}</p>
            </Button>
          )}
        </div>
      </main>
    </Col>
  );
};
